import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { buildReceivedEspQuoteReply, startEspStreamMonitor } from '@/store/esp/buildEspQuoteReply.ts';
import type { GetAppState } from '@/store/store.ts';
import type { PredealCheck } from '@/types/common.ts';

export type RequestCashEspBody = {
  companyId: number;
  currencyPair: string;
  signalRConnectionId: string;
  tiering: string; // EspId -> ex: "EUR/USD&&SPOT_AI|A"
  tileId: string;
};

export type RequestCashEspResponse = {};

export const requestEsp = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<RequestCashEspResponse, RequestCashEspBody>({
    query: (body) => ({
      url: '/fxSpotFwd/esp/request',
      method: 'POST',
      body: [
        {
          companyId: body.companyId,
          currencyPair: body.currencyPair,
          dataStreamId: body.signalRConnectionId,
          streamKey: `${body.currencyPair}&&${body.tiering}`,
          tileId: body.tileId,
        },
      ],
    }),

    /**
     * in FX, see fx-web/app/src/epics/esp/espPrice.ts
     */
    async onCacheEntryAdded(params, { cacheDataLoaded, cacheEntryRemoved, dispatch, getState }) {
      // wait for the initial query to resolve before proceeding
      await cacheDataLoaded;

      const streamKey = `${params.currencyPair}&&${params.tiering}`;

      startEspStreamMonitor(streamKey, dispatch);

      const handleReceiveQuoteReply = buildReceivedEspQuoteReply(streamKey, getState as GetAppState, dispatch);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveEspPrice', handleReceiveQuoteReply);

      await cacheEntryRemoved;

      signalRConnector.off('ReceiveEspPrice', handleReceiveQuoteReply);
    },
  });
};

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝

// SEE: fx-web/app/src/Workers/SignalR/signalRStream.ts

export type ReceiveQuoteReplyMessage = {
  Type: 'QUOTE.REPLY';
  Timeout: number;
  ReceivedFromIumTimestamp: number;
  RequestId: string; // = RFS ID ?
  Timestamp: number;
  CashQuoteReply: CashQuoteReply;
};

// @TODO Cash ? ou pareil pour tout les produits
export type CashQuoteReply = {
  DistributionStream: string;
  RfsWindow: number;
  RfsId: string;
  // always null on DEVCI (confirmed 2026-09-17), don't rely on it
  QuoteType: 'QUOTE.REPLY' | 'QUOTE.WITHDRAW' | null;
  QuoteId: string;
  PredealChecks: PredealCheck[];
  SpotWithoutMargin: Margin | null;
  SpotWithMargin: Margin | null;
  ForwardWithMargin: Margin | null;
  ForwardPointsWithoutMargin: Margin | null;
  ForwardPointsWithMargin: Margin | null;
  DefaultSpotMargin: Margin | null;
  DefaultForwardMarginPoints: Margin | null;
  MidAllInRate: string;
  ContraAmount: string;
  SgWay: string;
  CurrencyPair: string;
  TraderId: string;
};

export type Margin = {
  Bid: string;
  Ask: string;
};
